import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import {
  AlertCircle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shadcn/ui/dialog'
import { Button } from '@/features/shadcn/ui/button'
import { cn } from '@/features/utils'
import { getAxiosDefaultConfig } from '../../../modules/utils'
import { inkfrogListingsImportUrl } from '../../../modules/urls'
import { bumpInkfrogListingsStatsNonce } from '../integrationsSlice'

type Props = {
  open: boolean
  closeModal: () => void
}

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024 // 50 MB — matches server limit

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isCsvFile(file: File): boolean {
  const name = file.name.toLowerCase()
  return name.endsWith('.csv')
}

export default function InkfrogListingsUploadModal({ open, closeModal }: Props) {
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{
    rowCount: number
    importId: string
    sourceRowCount?: number
  } | null>(null)
  const [progress, setProgress] = useState(0)

  const clear = useCallback(() => {
    setFile(null)
    setError(null)
    setSuccess(null)
    setProgress(0)
    setDragActive(false)
    if (inputRef.current) inputRef.current.value = ''
  }, [])

  useEffect(() => {
    if (!open) {
      clear()
      setLoading(false)
    }
  }, [open, clear])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (loading) return
      clear()
      closeModal()
    }
  }

  const assignFile = (f: File | undefined | null) => {
    if (!f) return
    if (!isCsvFile(f)) {
      setError('Please use a .csv file exported from Inkfrog.')
      return
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(`File is too large (${formatFileSize(f.size)}). Maximum allowed size is 50 MB.`)
      return
    }
    setError(null)
    setSuccess(null)
    setFile(f)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!file) {
      setError('Choose or drop a CSV file exported from Inkfrog.')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    setLoading(true)
    setProgress(0)
    try {
      const res = await axios.post<{
        importId: string
        rowCount: number
        sourceRowCount?: number
        upsert?: boolean
      }>(
        inkfrogListingsImportUrl,
        formData,
        {
          ...getAxiosDefaultConfig(),
          onUploadProgress: (ev) => {
            if (ev.total && ev.total > 0) {
              setProgress(Math.min(100, Math.round((ev.loaded / ev.total) * 100)))
            }
          },
        }
      )
      const { importId, rowCount, sourceRowCount } = res.data
      setSuccess({ rowCount, importId, sourceRowCount })
      dispatch(bumpInkfrogListingsStatsNonce())
      setFile(null)
      if (inputRef.current) inputRef.current.value = ''
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string }; status?: number } }
      const msg =
        ax.response?.data?.error ||
        (typeof ax.response?.data === 'string' ? ax.response.data : null) ||
        'Upload failed. Check that the file is an Inkfrog listings CSV export.'
      setError(msg)
    } finally {
      setLoading(false)
      setProgress(0)
    }
  }

  const showProgressPanel = loading && file
  const uploadReachedServer = progress >= 100 && loading
  const byteProgressVisible = progress > 0 && progress < 100 && loading
  const startingUpload = loading && progress === 0 && !uploadReachedServer

  const blockDismissWhileLoading = loading
    ? {
        onInteractOutside: (e: Event) => e.preventDefault(),
        onPointerDownOutside: (e: Event) => e.preventDefault(),
        onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault(),
      }
    : {}

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose={loading}
        className="!tw-flex sm:tw-max-w-[460px] tw-flex-col"
        {...blockDismissWhileLoading}
      >
        <form
          onSubmit={handleSubmit}
          className="tw-flex tw-min-h-0 tw-flex-col tw-gap-6"
        >
          <DialogHeader className="tw-shrink-0 tw-text-left">
            <DialogTitle asChild>
              <h2 className="tw-text-lg tw-font-semibold tw-leading-none tw-tracking-tight tw-text-left">
                Inkfrog listings (CSV)
              </h2>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="tw-text-sm tw-text-slate-500 tw-text-left dark:tw-text-slate-400">
                Upload the listings CSV from Inkfrog (eBay listing format). Each upload is stored as its own import so
                you can keep catalog data as Inkfrog winds down.
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="tw-min-h-0 tw-flex-1 tw-space-y-4">
            {error && (
              <div
                className="tw-flex tw-gap-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-900"
                role="alert"
              >
                <AlertCircle className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-red-600" aria-hidden />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div
                className="tw-flex tw-gap-3 tw-rounded-lg tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-py-2.5 tw-text-sm tw-text-emerald-950"
                role="status"
              >
                <CheckCircle2 className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-emerald-600" aria-hidden />
                <div className="tw-min-w-0 tw-space-y-1">
                  <p className="tw-font-medium">
                    Processed {success.rowCount} listing{success.rowCount === 1 ? '' : 's'}{' '}
                    <span className="tw-font-normal tw-text-emerald-900/80">
                      (same Inkfrog ID or SKU updates existing rows instead of duplicating)
                    </span>
                  </p>
                  {success.sourceRowCount != null && success.sourceRowCount > success.rowCount ? (
                    <p className="tw-text-xs tw-text-emerald-800/90">
                      CSV had {success.sourceRowCount} rows; {success.sourceRowCount - success.rowCount} duplicate key
                      line
                      {success.sourceRowCount - success.rowCount === 1 ? '' : 's'} in this file were merged.
                    </p>
                  ) : null}
                  <p className="tw-font-mono tw-text-xs tw-text-emerald-800/90 tw-break-all">{success.importId}</p>
                </div>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv,application/vnd.ms-excel"
              className="tw-hidden"
              disabled={loading}
              onChange={(ev) => assignFile(ev.target.files?.[0])}
            />

            {!loading && (
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault()
                    inputRef.current?.click()
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault()
                  setDragActive(true)
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragActive(true)
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  setDragActive(false)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragActive(false)
                  assignFile(e.dataTransfer.files?.[0])
                }}
                onClick={() => inputRef.current?.click()}
                className={cn(
                  'tw-flex tw-min-h-[140px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border-2 tw-border-dashed tw-px-4 tw-py-8 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500 focus-visible:tw-ring-offset-2',
                  dragActive
                    ? 'tw-border-blue-400 tw-bg-blue-50/80'
                    : 'tw-border-slate-300 tw-bg-slate-50/50 hover:tw-border-slate-400 hover:tw-bg-slate-50',
                )}
              >
                <Upload className="tw-mb-2 tw-h-9 tw-w-9 tw-text-slate-400" strokeWidth={1.5} aria-hidden />
                <p className="tw-text-center tw-text-sm tw-font-medium tw-text-slate-700">
                  Drop CSV here or <span className="tw-text-blue-600">browse</span>
                </p>
                <p className="tw-mt-1 tw-text-center tw-text-xs tw-text-slate-500">Inkfrog listings export (.csv)</p>
              </div>
            )}

            {file && !loading && (
              <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2">
                <FileSpreadsheet className="tw-h-8 tw-w-8 tw-shrink-0 tw-text-emerald-700" aria-hidden />
                <div className="tw-min-w-0 tw-flex-1">
                  <p className="tw-truncate tw-text-sm tw-font-medium tw-text-slate-900" title={file.name}>
                    {file.name}
                  </p>
                  <p className="tw-text-xs tw-text-slate-500">{formatFileSize(file.size)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="tw-shrink-0 tw-text-slate-500 hover:tw-text-red-600"
                  onClick={(ev) => {
                    ev.stopPropagation()
                    setFile(null)
                    setError(null)
                    if (inputRef.current) inputRef.current.value = ''
                  }}
                  aria-label="Remove file"
                >
                  <Trash2 className="tw-h-4 tw-w-4" />
                </Button>
              </div>
            )}

            {showProgressPanel && (
              <div className="tw-space-y-3 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50/80 tw-p-4">
                <div className="tw-flex tw-items-start tw-gap-3">
                  <FileSpreadsheet className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-emerald-700" aria-hidden />
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-truncate tw-text-sm tw-font-medium tw-text-slate-900" title={file!.name}>
                      {file!.name}
                    </p>
                    <p className="tw-text-xs tw-text-slate-500">{formatFileSize(file!.size)}</p>
                  </div>
                </div>

                <div className="tw-space-y-1.5">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs tw-font-medium tw-text-slate-600">
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                      <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin tw-text-blue-600" aria-hidden />
                      {uploadReachedServer
                        ? 'Saving listings…'
                        : byteProgressVisible
                          ? 'Uploading…'
                          : 'Starting upload…'}
                    </span>
                    {byteProgressVisible ? (
                      <span className="tw-tabular-nums tw-text-slate-700">{progress}%</span>
                    ) : null}
                  </div>

                  <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-slate-200">
                    {byteProgressVisible ? (
                      <div
                        className="tw-h-full tw-rounded-full tw-bg-blue-600 tw-transition-[width] tw-duration-300 tw-ease-out"
                        style={{ width: `${progress}%` }}
                      />
                    ) : uploadReachedServer ? (
                      <div className="tw-h-full tw-w-full tw-animate-pulse tw-rounded-full tw-bg-emerald-500" />
                    ) : startingUpload ? (
                      <div className="tw-h-full tw-w-full tw-animate-pulse tw-rounded-full tw-bg-blue-500/90" />
                    ) : null}
                  </div>

                  {uploadReachedServer ? (
                    <p className="tw-text-[11px] tw-leading-snug tw-text-slate-500">
                      File received — writing rows. Large exports may take a moment.
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="tw-shrink-0 tw-gap-2 tw-border-t tw-border-slate-100 tw-pt-4 sm:tw-gap-2">
            <Button type="button" variant="outline" disabled={loading} onClick={() => handleOpenChange(false)}>
              Close
            </Button>
            <Button type="submit" disabled={loading || !file}>
              {loading ? (
                <>
                  <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
                  Uploading…
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
